import { find, mapValues, orderBy, uniqBy } from 'lodash';
import { createSelector } from 'reselect';

import { selectConfigEngines } from '@common-modules/engines/selectors';
import {
  CREATED_DATE,
  LAST_MODIFIED_DATE,
  MainPageStore,
  SummaryTDO,
  StructuredDataObject,
} from './models';
import { namespace } from './store';
import {
  selectRouteQuery,
  selectCurrentRoutePayload,
} from '@common-modules/routing';
import { TDOId } from '@common-modules/universal/models/Brands';
import { ElementOf } from 'ts-essentials';
import { modules } from '@veritone/glc-redux';
import type { TaskStatus } from 'veritone-types';
import { selectRedactFiles } from '@common/state/modules/redactFile';
import {
  getRedactionStatusIdentity,
  matchesRedactionStatusFilter,
} from '@common-modules/mediaDetails/redactionStatuses';
import { IN_REDACTION_TAG_KEY } from '@common-modules/mediaDetails';
import { selectRedactionCustomStatuses } from '@redact-modules/redactionStatuses/selectors';

const {
  config: { getConfig },
} = modules;

export const selectStore = (store: {
  [namespace]: MainPageStore;
}): MainPageStore => store[namespace];

export const selectFilter = createSelector(selectStore, (s) => s.filter);

// VE-23391: TDO ids uploaded this session — used to flag freshly-added tiles
// with a "New" pill.
export const selectNewlyAddedTdoIds = createSelector(
  selectStore,
  (s): string[] => s?.newlyAddedTdoIds ?? []
);

// VE-24901: the most recently opened TDO — used to flag its tile with a "Last
// Opened" pill, mirroring the folder view.
export const selectLastOpenedTdoId = createSelector(
  selectStore,
  (s): string | undefined => s?.lastOpenedTdoId
);

export const selectIsFilePickerOpen = createSelector(selectStore, (s) => ({
  isShowUploadForm: s.isFilePickerOpen,
  isFilePickerUrlOpen: s.isFilePickerUrlOpen,
}));

/** ************************************
 * TDO Selectors
 */

export const selectTDOs = createSelector(selectStore, (s) => s.tdos);

export const selectTDOsArray = createSelector(selectTDOs, (s) =>
  Object.values<Readonly<SummaryTDO>>(s)
);

/**
 * Returns a TDO by id.
 */
export const selectTDOById = (id: TDOId) =>
  createSelector(selectTDOs, (tdos) => tdos[id]);

export const selectIsReadyTDOById = (id: TDOId) =>
  createSelector(selectTDOById(id), (tdo) => tdo?.status === 'recorded');

// FILTER-TODO - we really should filter on the sdo by status instead of the current tdos open
export const selectTDOsArrayByCurrentFilter = createSelector(
  selectTDOsArray,
  selectFilter,
  selectRedactionCustomStatuses,
  (tdos, filter, customStatuses) => {
    switch (filter) {
      case LAST_MODIFIED_DATE:
        return orderBy(tdos, 'modifiedDateTime', 'desc');
      case CREATED_DATE:
        return orderBy(tdos, 'createdDateTime', 'desc');
      default:
        // Status filter: well-known key or custom statusKey (VE-24546).
        return tdos.filter((tdo) => {
          const redactionStatusTag = (tdo.details?.tags || []).find(
            (tag) =>
              tag?.value === IN_REDACTION_TAG_KEY && !!tag.redactionStatus
          );
          return matchesRedactionStatusFilter(
            redactionStatusTag
              ? getRedactionStatusIdentity(redactionStatusTag)
              : undefined,
            filter,
            customStatuses
          );
        });
    }
  }
);

type Status =
  | 'failed'
  | 'retryable'
  | 'waiting'
  | 'running'
  | 'complete'
  | 'none'
  | 'aborted';

const DETECTION_EST_MULTI = 4;
const TRACKING_EST_MULTI = 1;
const TRANSCRIPTION_EST_MULTI = 0.5;
const VOICE_MASK_EST_MULTI = 1.5;
const SPEAKER_SEPARATION_EST_MULTI = 0.5;
/**
 * Returns SDOs in store.
 */
export const selectSDOs = createSelector(selectStore, (s) => s?.sdos || {});

export const selectListTDOsUploading = createSelector(
  selectStore,
  (s) => s.listTDOsUploading
);

export const selectTaskHistory = createSelector(
  selectTDOs,
  selectConfigEngines,
  selectSDOs,
  selectListTDOsUploading,
  selectRedactFiles,
  (
    tdos,
    {
      detectionEngineIds,
      downloadEngineCategoryId,
      downloadEngineId,
      opticalTrackingEngineId,
      transcriptionCategoryId,
      transcriptionEngineId,
      redactEngineId,
      legacyRedactEngineId,
      glcIngestionEngineId,
      detectionCategory,
      webstreamAdapterEngineId,
      streamIngestorDASHPlaybackSegmentCreatorId,
      streamIngestorPlaybackSegmentCreatorId,
      voiceMaskEngineId,
      pdfConverterEngineId,
      documentOcrEngineId,
      documentPIIDetectionEngineId,
      speakerSeparationEngineId,
      speakerDetectionCategoryId,
    },
    sdos,
    listTDOsUploading,
    redactFiles
  ) =>
    mapValues(tdos, (tdo: SummaryTDO) => {
      const sdo = sdos[tdo.id];
      if (!sdo) {
        return {};
      }
      const duration =
        +new Date(tdo.stopDateTime) - +new Date(tdo.startDateTime);
      const tasks = tdo.tasks?.records || [];
      const engineCategoriesRun = sdo?.data?.engineCategoriesRun || [];
      let transcriptionStatus = 'none';
      let faceTaskStatus = 'none';
      let speakerSeparationStatus = 'none';
      // If there are no tasks run for the TDO and only has engine results copied from the parent TDO then its being caught here and icons are displayed in the thumbnail.
      if (tasks.length === 0 && engineCategoriesRun.length) {
        if (engineCategoriesRun.includes(transcriptionCategoryId)) {
          transcriptionStatus = 'complete';
        }
        if (engineCategoriesRun.includes(detectionCategory)) {
          faceTaskStatus = 'complete';
        }
        if (
          speakerDetectionCategoryId &&
          engineCategoriesRun.includes(speakerDetectionCategoryId)
        ) {
          speakerSeparationStatus = 'complete';
        }
        return {
          faceTaskStatus,
          redactTaskStatus: 'none',
          trackingTaskStatus: 'none',
          transcriptionStatus,
          voiceMaskTaskStatus: 'none',
          speakerSeparationStatus,
          downloadStatus: 'none',
          estimatedEndTime: 0,
          ingestionStatus: 'none',
        };
      }

      // use for render upload failed after retry createJob 3 times.
      // VE-23391: a `recorded` TDO ingested successfully even if it ran no
      // engine tasks (fast PDF upload attaches the media asset directly), so
      // don't mistake its lack of tasks for a failed upload.
      if (
        tasks.length === 0 &&
        engineCategoriesRun.length === 0 &&
        !listTDOsUploading[tdo.id] &&
        tdo.status !== 'recorded'
      ) {
        return {
          faceTaskStatus,
          redactTaskStatus: 'none',
          trackingTaskStatus: 'none',
          transcriptionStatus,
          voiceMaskTaskStatus: 'none',
          speakerSeparationStatus,
          downloadStatus: 'none',
          estimatedEndTime: 0,
          ingestionStatus: 'failed',
        };
      }

      // include legacy first stage ingestion engines in case old TDO's still around
      const ingestionEngineIds = [
        glcIngestionEngineId,
        webstreamAdapterEngineId,
      ];

      // TODO: This logic iterates through all the unique tasks and
      // fires getState fn-n many times for each iteration.
      const [
        faceTask,
        trackingTask,
        transcriptionTask,
        voiceMaskTask,
        redactTask,
        downloadTask,
        ingestionTask,
        si2DashTask,
        si2PlaybackTask,
        pdfConverterTask,
        ocrTask,
        piiTask,
        speakerSeparationTask,
      ] = uniqBy(tasks, (stdo) => stdo.engine?.id).reduce<
        // add status "downloadStatus" in TdoWrapper in MainPage
        //
        // One tuple slot per named task above, in the same order: face, track,
        // transcription, voiceMask, redact, download, ingestion, si2dash,
        // si2playback, pdfConverter, ocr, pii, speakerSeparation. Adding an
        // engine means adding a slot here, a seed entry below, and a getState
        // call between them.
        [
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
          [Status, number],
        ]
      >(
        (
          [
            face,
            track,
            transcription,
            voiceMask,
            redact,
            download,
            ingestion,
            si2dash,
            si2playback,
            pdfConverter,
            ocr,
            pii,
            speakerSeparation,
          ],
          engineRun
        ) => [
          getState(
            detectionEngineIds,
            detectionCategory,
            [DETECTION_EST_MULTI, duration],
            engineRun,
            face,
            sdo
          ),
          getState(
            [opticalTrackingEngineId],
            null,
            [TRACKING_EST_MULTI, duration],
            engineRun,
            track,
            sdo
          ),
          getState(
            [transcriptionEngineId],
            transcriptionCategoryId,
            [TRANSCRIPTION_EST_MULTI, duration],
            engineRun,
            transcription,
            sdo
          ),
          getState(
            [voiceMaskEngineId],
            null,
            [VOICE_MASK_EST_MULTI, duration],
            engineRun,
            voiceMask,
            sdo
          ),
          getState(
            [redactEngineId, legacyRedactEngineId],
            null,
            [0, duration],
            engineRun,
            redact,
            sdo
          ),
          getState(
            [downloadEngineId],
            downloadEngineCategoryId,
            [0, duration],
            engineRun,
            download,
            sdo
          ),
          getState(
            ingestionEngineIds,
            null,
            [0, duration],
            engineRun,
            ingestion,
            sdo
          ),
          getState(
            [streamIngestorDASHPlaybackSegmentCreatorId],
            null,
            [0, duration],
            engineRun,
            si2dash,
            sdo
          ),
          getState(
            [streamIngestorPlaybackSegmentCreatorId],
            null,
            [0, duration],
            engineRun,
            si2playback,
            sdo
          ),
          getState(
            pdfConverterEngineId ? [pdfConverterEngineId] : [],
            null,
            [0, duration],
            engineRun,
            pdfConverter,
            sdo
          ),
          getState(
            documentOcrEngineId ? [documentOcrEngineId] : [],
            null,
            [0, duration],
            engineRun,
            ocr,
            sdo
          ),
          getState(
            documentPIIDetectionEngineId ? [documentPIIDetectionEngineId] : [],
            null,
            [0, duration],
            engineRun,
            pii,
            sdo
          ),
          getState(
            speakerSeparationEngineId ? [speakerSeparationEngineId] : [],
            speakerDetectionCategoryId,
            [SPEAKER_SEPARATION_EST_MULTI, duration],
            engineRun,
            speakerSeparation,
            sdo
          ),
        ],
        [
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
          ['none', 0],
        ]
      );

      // combine all ingestion status - including legacy ingestion engines
      let ingestionStatus = ingestionTask[0];
      if (si2DashTask[0] === 'failed' || si2PlaybackTask[0] === 'failed') {
        ingestionStatus = 'failed';
      }
      if (ingestionStatus === 'none' && pdfConverterTask[0] !== 'none') {
        ingestionStatus = pdfConverterTask[0];
      }
      if (pdfConverterTask[0] === 'failed') {
        ingestionStatus = 'failed';
      }

      // give preference to the status on redactFiles object (otherwise it may appear the redacted file is missing because the general task update UPDATE_ORG_TDOS_TASKS does not update assets on tdos)
      //
      // VE-28429: only `running` is taken from the duck, matching the folder
      // view's rule in buildTileTaskInfo. Letting it win unconditionally meant a
      // `complete` — or worse, a STUCK `running` — entry masked the engine's own
      // answer forever, and since the image tile now reads this status it would
      // hide the settled "Redacted" chip with no way back.
      const duckStatus = redactFiles[tdo.id]?.status;
      const redactTaskStatus =
        duckStatus === 'running' ? duckStatus : redactTask[0] || duckStatus;

      return {
        faceTaskStatus: faceTask[0],
        redactTaskStatus: redactTaskStatus,
        trackingTaskStatus: trackingTask[0],
        transcriptionStatus: transcriptionTask[0],
        voiceMaskTaskStatus: voiceMaskTask[0],
        ocrTaskStatus: ocrTask[0],
        piiTaskStatus: piiTask[0],
        speakerSeparationStatus: speakerSeparationTask[0],
        ingestionStatus: ingestionStatus,
        downloadStatus: downloadTask[0],
        estimatedEndTime: [
          faceTask,
          trackingTask,
          transcriptionTask,
          // redactTask[1]
        ].reduce(
          (est, [s, t]) => (s === 'waiting' ? Infinity : Math.max(t, est)),
          0
        ),
      };
    })
);

const getState = (
  id: string[],
  catId: string | null | undefined,
  [multi, duration]: [number, number],
  {
    status,
    // taskOutput,
    createdDateTime,
    startedDateTime,
    engine,
  }: ElementOf<SummaryTDO['tasks']['records']>,
  [lastStatus, lastMs]: [Status, number],
  sdo: StructuredDataObject
): [Status, number] => {
  if (id.includes(engine.id) || catId === engine.categoryId) {
    return isEngineWaiting(status)
      ? ['waiting', Date.now() - +new Date(createdDateTime)]
      : isEngineRunning(status)
        ? [
            'running',
            calcEst(duration, multi, startedDateTime || createdDateTime),
          ]
        : isEngineComplete(status)
          ? ['complete', 0]
          : // : isEngineFailureRetryable(taskOutput)
            // ? ['aborted', 0]
            isEngineAborted(status)
            ? ['aborted', 0] // Change to 'retryable' when implemented.
            : isEngineFailed(status)
              ? ['failed', 0]
              : [lastStatus, lastMs];
  } else {
    // If the Engine results was copied from the parent TDO. The meta data in the SDO is used to determine the status.
    // NOTE: Tasks cannot be copied over like engine results.
    const engineCategoriesRun = sdo?.data?.engineCategoriesRun || [];
    if (catId && engineCategoriesRun.includes(catId)) {
      return ['complete', 0];
    }
    return [lastStatus, lastMs];
  }
};

const calcEst = (duration: number, multi: number, time: string) =>
  Math.max((duration - (Date.now() - +new Date(time))) * multi || 1, 1);

// TODO: veritone-types incorrectly exports TaskStatus enum from pure definition file - replace
// so that we can use the enum directly instead of casting to string
const isEngineWaiting = (status: TaskStatus) =>
  (status as string) === 'accepted' ||
  (status as string) === 'pending' ||
  (status as string) === 'queued';
const isEngineRunning = (status: TaskStatus) =>
  (status as string) === 'pending' ||
  (status as string) === 'queued' ||
  (status as string) === 'running';

const isEngineComplete = (status: TaskStatus) =>
  (status as string) === 'complete';

const isEngineAborted = (status: TaskStatus) =>
  (status as string) === 'cancelled';

const isEngineFailed = (status: TaskStatus) =>
  !isEngineRunning(status) && !isEngineComplete(status);

// const isEngineFailureRetryable = (
//   taskOutput: SummaryTDO['tasks']['records'][number]['taskOutput']
// ) => isString(taskOutput?.error) && taskOutput?.error.includes('RESOURCE:CPU');

/** ************************************
 * SDO Selectors
 */

/**
 * Returns a SDO by tdoId.
 */
export const selectSDOById = (tdoId: TDOId) =>
  createSelector(selectSDOs, (s) => s[tdoId]);

/**
 * Returns a SDO by sdoId.
 */
export const selectSDOBySDOId = (sdoId: string) =>
  createSelector(selectSDOsArray, (sdos) => find(sdos, { id: sdoId }));

/**
 * Returns SDOs as array.
 */
export const selectSDOsArray = createSelector(selectSDOs, (sdos) =>
  Object.values(sdos).map((s) => ({
    id: s.id,
    tdoId: s.data.tdoId,
    statusKey: s.data.statusKey,
  }))
);

/**
 * Returns the current pagination (infinite scrolling) state.
 */
export const selectPagination = createSelector(selectStore, (s) => ({
  currentPage: s.currentPage,
  itemsPerPage: s.itemsPerPage,
  hasMore: s.hasMore,
  loadingNextPage: s.loadingNextPage,
  invalidSDOCount: s.invalidSDOCount,
  sdoOffset: s.sdoOffset,
}));

/**
 * VE-26729: whether something off the Home route (a Trash restore) changed what Home
 * should show, so the next entry to Home must refetch rather than trust the store.
 */
export const selectHomeListStale = createSelector(
  selectStore,
  (s) => s.homeListStale
);

/** ************************************
 * STORE Status Selectors
 */

export const TDOS_LOADING = 'tdos_loading';
export const TDOS_EMPTY = 'tdos_empty';
export const TDOS_EMPTY_BY_FILTER = 'tdos_empty_by_filter';
export const TDOS_READY = 'tdos_ready';
export const TDOS_REFRESH = 'tdos_refresh';

// /**
//  *
//  * @returns TDOS_EMPTY | TDOS_EMPTY_BY_FILTER | TDOS_LOADING | TDOS_READY | TDOS_REFRESH
//  */
export const selectStoreStatus = createSelector(
  selectStore,
  selectTDOsArray,
  selectTDOsArrayByCurrentFilter,
  (a, b, c) => {
    const { fetchingOrganizationTdos, loaded } = a;
    if (fetchingOrganizationTdos && !loaded) {
      return [TDOS_LOADING];
    }
    let status = [TDOS_READY];
    if (fetchingOrganizationTdos) {
      status = [...status, TDOS_REFRESH];
    }
    if (b.length === 0) {
      status = [...status, TDOS_EMPTY];
    } else if (c.length === 0) {
      status = [...status, TDOS_EMPTY_BY_FILTER];
    }
    return status;
  }
);

export const selectIsLoaded = createSelector(selectStore, (s) => s.loaded);

export const selectFetchOrganizationTDOsLoading = createSelector(
  selectStore,
  (s) => s.fetchingOrganizationTdos
);

export const selectIsLoading = createSelector(
  selectIsLoaded,
  selectTDOsArray,
  selectFetchOrganizationTDOsLoading,
  (a, b, c) => !a && (b.length === 0 || c)
);

/**
 * Selects if an error occurred.
 */
export const selectError = createSelector(selectStore, (s) => s.error);

export const selectSelectedTDOs = createSelector(
  selectStore,
  (s) => s.selectedTDOs
);

export const selectSelectedTDOsArray = createSelector(
  selectSelectedTDOs,
  (selectedTDOs) =>
    (Object.keys(selectedTDOs) as Array<keyof typeof selectedTDOs>).filter(
      (id) => selectedTDOs[id] === true
    )
);

export const selectSelectedTDOCount = createSelector(
  selectSelectedTDOsArray,
  (tdoIds) => tdoIds.length
);

export const selectRequestIds = createSelector(
  selectRouteQuery,
  selectCurrentRoutePayload,
  // TODO: Can we type routeQuery so these casts aren't necessary?
  (routeQuery, payload) => ({
    govQARequestId:
      (payload?.govQARequestId as string | undefined) ||
      (routeQuery?.govqa_request_id as string | undefined),
    foiaXpressRequestId: routeQuery?.foiaxpress_request_id as
      | string
      | undefined,
    nuixRequestId: routeQuery?.nuix_request_id as string | undefined,
    casepointRequestId: routeQuery?.casepoint_request_id as string | undefined,
    exterroRequestId: routeQuery?.exterro_request_id as string | undefined,
    signedUrl: routeQuery?.url as string | undefined,
  })
);

// Presence of the `url` query key (regardless of its value) is what signals URL-ingest intent:
// `/ingest?url=<signed url>` seeds the modal, while `/ingest?url`, `?url=`, and `?url=""` open the
// modal with an empty, editable field (Ingest disabled). A bare `/ingest` with no `url` key falls
// through to the regular file picker. Gating on the value's truthiness would collapse those cases.
export const selectHasUrlParam = createSelector(
  selectRouteQuery,
  (routeQuery) => (routeQuery ? 'url' in routeQuery : false)
);

export const selectGovQARequestId = createSelector(
  selectRouteQuery,
  selectCurrentRoutePayload,
  (routeQuery, payload) => ({
    govQARequestId:
      (payload?.govQARequestId as string | undefined) ||
      (routeQuery?.govqa_request_id as string | undefined),
    signedUrl: routeQuery?.url as string | undefined,
  })
);

export const selectFOIAXpressRequestId = createSelector(
  selectRouteQuery,
  (routeQuery) => ({
    // TODO: Should we be validating that this is a string?
    foiaXpressRequestId: routeQuery?.foiaxpress_request_id as
      | string
      | undefined,
    signedUrl: routeQuery?.url as string | undefined,
  })
);

export const selectCasepointRequestId = createSelector(
  selectRouteQuery,
  (routeQuery) => ({
    // TODO: Should we be validating that this is a string?
    casepointRequestId: routeQuery?.casepoint_request_id as string | undefined,
    signedUrl: routeQuery?.url as string | undefined,
  })
);

export const selectNuixRequestId = createSelector(
  selectRouteQuery,
  (routeQuery) => ({
    nuixRequestId: routeQuery?.nuix_request_id as string | undefined,
    signedUrl: routeQuery?.url as string | undefined,
  })
);

export const selectExterroRequestId = createSelector(
  selectRouteQuery,
  (routeQuery) => ({
    // TODO: Should we be validating that this is a string?
    exterroRequestId: routeQuery?.exterro_request_id as string | undefined,
    signedUrl: routeQuery?.url as string | undefined,
  })
);
export const selectUploadSizeLimit = (state: any) =>
  getConfig<Window['config']>(state)?.uploadSizeLimit;
